import { ScrapbookSaveItem } from '@/types';
import { buildAPIUrl, buildAPIPipeUrl, responseInterceptor } from '.';

export interface ScrapbookFolder {
  id: number;
  name: string;
  note: string;
  path: string;
  isShared: boolean;
  hasSharing: boolean;
  sharer: string;
  parentId: number;
  children?: ScrapbookFolder[];
}

export interface ScrapbookFile {
  id?: number;
  name: string;
  content?: any;
  type?: string;
  contentType?: string;
  dateCreated?: string;
  dateModified?: string;
  lastModified?: string;
  isBlob?: boolean;
  blobUri?: string | null;
  sel?: boolean; // Added for UI state
}

export interface ListFolderResponse {
  newFolderId: number;
  rootFolder: ScrapbookFolder;
  files: ScrapbookFile[];
}

export const listFolder = async (folderId?: number): Promise<ListFolderResponse> => {
  const folderIdParam = folderId !== undefined && folderId >= 0 ? `?folderId=${folderId}` : '';
  const response = await responseInterceptor(
    await fetch(`${await buildAPIUrl()}/services/scrapbook/listFolder${folderIdParam}`),
  );

  return response.json();
};

export const createFolder = async (
  folderId: number,
  name: string,
): Promise<{ succeeded: boolean; message?: string }> => {
  const encodedName = encodeURIComponent(name);
  const response = await responseInterceptor(
    await fetch(`${await buildAPIUrl()}/services/scrapbook/createFolder2?folderId=${folderId}&name=${encodedName}`, {
      method: 'POST',
    }),
  );

  return response.json();
};

export const createFolderWithPath = async (
  folderId: number,
  path: string,
): Promise<{ succeeded: boolean; message?: string }> => {
  const encodedPath = encodeURIComponent(path);
  const response = await responseInterceptor(
    await fetch(`${await buildAPIUrl()}/services/scrapbook/createFolder?path=${encodedPath}&folderId=${folderId}`, {
      method: 'POST',
    }),
  );

  return response.json();
};

export const renameFolder = async (
  folderId: number,
  newName: string,
): Promise<{ succeeded: boolean; message?: string }> => {
  const encodedName = encodeURIComponent(newName);
  const response = await responseInterceptor(
    await fetch(`${await buildAPIUrl()}/services/scrapbook/renameFolder?folderId=${folderId}&newName=${encodedName}`, {
      method: 'POST',
    }),
  );

  return response.json();
};

export const deleteFolder = async (folderId: number): Promise<{ succeeded: boolean; message?: string }> => {
  const response = await responseInterceptor(
    await fetch(`${await buildAPIUrl()}/services/scrapbook/deleteFolder2?folderId=${folderId}`, {
      method: 'POST',
    }),
  );

  return response.json();
};

export const copyFolder = async (
  srcFolderId: number,
  dstFolderId: number,
  userId?: string,
): Promise<{ succeeded: boolean; message?: string }> => {
  const response = await responseInterceptor(
    await fetch(`${await buildAPIUrl()}/services/scrapbook/copyFolder`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        userId,
        srcFolderId,
        dstFolderId,
      }),
    }),
  );

  return response.json();
};

export const moveFolder = async (
  srcFolderId: number,
  dstFolderId: number,
  userId?: string,
): Promise<{ succeeded: boolean; message?: string }> => {
  const response = await responseInterceptor(
    await fetch(`${await buildAPIUrl()}/services/scrapbook/moveFolder`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        userId,
        srcFolderId,
        dstFolderId,
      }),
    }),
  );

  return response.json();
};

export const updateFolderNote = async (
  folderId: number,
  note: string | null,
): Promise<{ succeeded: boolean; message?: string }> => {
  const response = await responseInterceptor(
    await fetch(`${await buildAPIUrl()}/services/scrapbook/editFolderNote`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        folderId,
        note,
      }),
    }),
  );

  return response.json();
};

export const saveToScrapbook = async (
  folderId: number,
  saveObject: ScrapbookSaveItem,
  userId?: string,
): Promise<{ succeeded: boolean; message?: string; newFolderId?: number; newPath?: string }> => {
  const response = await responseInterceptor(
    await fetch(`${await buildAPIUrl()}/services/scrapbook/writeText`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        folderId,
        ...saveObject,
        userId,
      }),
    }),
  );

  return response.json();
};

export const saveBulkToScrapbook = async (
  folderId: number,
  contentList: ScrapbookSaveItem[],
  userId?: string,
): Promise<{ succeeded: boolean; message?: string }> => {
  const response = await responseInterceptor(
    await fetch(`${await buildAPIUrl()}/services/scrapbook/writeContent`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        folderId,
        contentList,
        userId,
      }),
    }),
  );

  return response.json();
};

export const deleteFiles = async (
  userId: string,
  folderId: number,
  path: string,
  files: { name: string; isBlob?: boolean }[],
): Promise<{ succeeded: boolean; message?: string }> => {
  const response = await responseInterceptor(
    await fetch(`${await buildAPIUrl()}/services/scrapbook/deleteFiles`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        userId,
        folderId,
        path,
        files,
      }),
    }),
  );

  return response.json();
};

export const copyFiles = async (
  userId: string,
  srcFolderId: number,
  dstFolderId: number,
  files: { name: string; isBlob?: boolean; contentType?: string }[],
): Promise<{ succeeded: boolean; message?: string; folderMovedTo?: string; alreadyExistingFileNames?: string[] }> => {
  const response = await responseInterceptor(
    await fetch(`${await buildAPIUrl()}/services/scrapbook/copyFiles`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        userId,
        srcFolderId,
        dstFolderId,
        files,
      }),
    }),
  );

  return response.json();
};

export const moveFiles = async (
  userId: string,
  srcFolderId: number,
  dstFolderId: number,
  files: { name: string; isBlob?: boolean; contentType?: string }[],
): Promise<{ succeeded: boolean; message?: string; folderMovedTo?: string; alreadyExistingFileNames?: string[] }> => {
  const response = await responseInterceptor(
    await fetch(`${await buildAPIUrl()}/services/scrapbook/moveFiles`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        userId,
        srcFolderId,
        dstFolderId,
        files,
      }),
    }),
  );

  return response.json();
};

export const downloadZip = async (
  userId: string,
  folderId: number,
  path: string,
  files: { name: string }[],
  folderName?: string,
): Promise<void> => {
  // Used /apipipe endpoint to avoid middleware JSON parsing issues with file downloads
  const response = await fetch(`${await buildAPIPipeUrl()}/services/scrapbook/downloadZip2`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: new URLSearchParams({
      jsonData: JSON.stringify({
        path,
        userId,
        folderId,
        files,
      }),
    }),
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(`Download failed: ${response.statusText} - ${text}`);
  }

  // Handle the file download
  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = folderName ? `${folderName}.zip` : 'scrapbook.zip';
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
};

export const getUploadSASs = async (
  userId: string,
  folderId: number,
  folder: string,
  files: string[],
): Promise<{ succeeded: boolean; uploadSASs?: { file: string; sas: string }[]; message?: string }> => {
  const response = await responseInterceptor(
    await fetch(`${await buildAPIUrl()}/services/scrapbook/getUploadSASs`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        userId,
        folderId,
        folder,
        files,
      }),
    }),
  );

  return response.json();
};

export const registerUpload = async (
  userId: string,
  folder: string,
  folderId: number,
  name: string,
  size: number,
  type: string,
): Promise<{ succeeded: boolean; message?: string }> => {
  const response = await responseInterceptor(
    await fetch(`${await buildAPIUrl()}/services/scrapbook/registerUpload`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        userId,
        folder,
        folderId,
        name,
        size,
        type,
      }),
    }),
  );

  return response.json();
};

export const getReportFromId = async (id: number, account?: string, userId?: string): Promise<any> => {
  const response = await responseInterceptor(
    await fetch(`${await buildAPIUrl()}/umbraco/Api/ReportListing/GetReportFromId`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ id, account, userId }),
    }),
  );

  return response.json();
};

export const getReportPreview = async (reportId: string): Promise<any> => {
  const response = await responseInterceptor(
    await fetch(`${await buildAPIUrl()}/umbraco/Api/ReportPreview/GetPreview?id=${reportId}`, {
      method: 'POST',
    }),
  );

  return response.json();
};

export const downloadImageById = async (imageId: string): Promise<string> => {
  return `${await buildAPIUrl()}/umbraco/api/ArticleTools/GetImage?imageId=${imageId}`;
};

// Sharing API functions
export interface ShareUser {
  guid: string;
  email: string;
  name?: string;
  company?: string;
  dataAdded?: string;
}

export const shareFolder = async (
  userId: string,
  folderId: number,
  sharedUserIds: string[],
): Promise<{ succeeded: boolean; message?: string }> => {
  const response = await responseInterceptor(
    await fetch(`${await buildAPIUrl()}/services/scrapbook/shareFolder`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        userId,
        folderId,
        sharedUserIds,
      }),
    }),
  );

  return response.json();
};

export const unshareFolder = async (folderId: number): Promise<{ succeeded: boolean; message?: string }> => {
  const response = await responseInterceptor(
    await fetch(`${await buildAPIUrl()}/services/scrapbook/unshareFolder`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        folderId,
      }),
    }),
  );

  return response.json();
};

export const listFolderShareUsers = async (
  userId: string,
  folderId: number,
): Promise<{ succeeded: boolean; foundUsers?: ShareUser[]; message?: string }> => {
  const response = await responseInterceptor(
    await fetch(`${await buildAPIUrl()}/services/scrapbook/listFolderShareUsers?userid=${userId}&folderId=${folderId}`),
  );

  return response.json();
};
export const lookupUser = async (
  email: string,
): Promise<{ succeeded: boolean; foundUsers?: ShareUser[]; message?: string }> => {
  const response = await responseInterceptor(
    await fetch(`${await buildAPIUrl()}/services/scrapbook/lookupUser?email=${encodeURIComponent(email)}`),
  );

  return response.json();
};
